// src/components/admin/SalonHomePage.jsx
// #Revised UI_v2
import React, { useState, useEffect } from 'react';
import { ref, getDownloadURL, listAll } from 'firebase/storage';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db, storage } from '../../utils/firebase';
import { useSalonLocations } from '../../hooks/useSalonLocations';
import '../../styles/salonhomepage.css';

// Get Monday 00:00:00 of the current week
const getWeekStart = () => {
  const now = new Date();
  const day = now.getDay(); // 0=Sun, 1=Mon...
  const diff = day === 0 ? 6 : day - 1; // shift so Monday=0
  const monday = new Date(now);
  monday.setDate(now.getDate() - diff);
  monday.setHours(0, 0, 0, 0);
  return monday;
};

// Get Sunday 23:59:59 of the current week
const getWeekEnd = () => {
  const start = getWeekStart();
  const sunday = new Date(start);
  sunday.setDate(start.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);
  return sunday;
};

// Get current month key like "2026-02"
const getCurrentMonthKey = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

// Get current ISO week key like "2026-W06"
const getCurrentISOWeek = () => {
  const now = new Date();
  const target = new Date(now.valueOf());
  // ISO week: nearest Thursday determines year
  const dayNum = (now.getDay() + 6) % 7; // Mon=0..Sun=6
  target.setDate(target.getDate() - dayNum + 3); // nearest Thursday
  const jan4 = new Date(target.getFullYear(), 0, 4);
  const dayOfYear = Math.round((target - jan4) / 86400000) + 1;
  const weekNum = Math.ceil(dayOfYear / 7);
  return `${target.getFullYear()}-W${String(weekNum).padStart(2, '0')}`;
};

function SalonHomePage({ salonId }) {
  const { locations, loading: locLoading } = useSalonLocations(salonId);
  const isMultiLocation = locations.length > 1;

  // null = All Locations; string = specific locationId
  const [selectedLocationId, setSelectedLocationId] = useState(null);

  const [imageUrl, setImageUrl] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [currentMonthRevenue, setCurrentMonthRevenue] = useState(0);
  const [currentWeekRevenue, setCurrentWeekRevenue] = useState(0);
  const [targets, setTargets] = useState({
    monthlyTarget: 0,
    weeklyTarget: 0,
    dailyTarget: 0,
    monthlyPeriodTargets: [],
    weeklyPeriodTargets: []
  });
  const [metricsLoading, setMetricsLoading] = useState(true);

  const [todayAppointments, setTodayAppointments] = useState(0);
  const [nextAppointment, setNextAppointment] = useState(null);
  const [pendingRequests, setPendingRequests] = useState(0);
  const [todaySchedule, setTodaySchedule] = useState([]);
  const [lastCampaign, setLastCampaign] = useState(null);
  const [operationalLoading, setOperationalLoading] = useState(true);

  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleCheckInOpen, setScheduleCheckInOpen] = useState(null);
  const [scheduleCheckInValue, setScheduleCheckInValue] = useState('Showed Up');
  const [selectedAppt, setSelectedAppt] = useState(null);
  const [showCampaignModal, setShowCampaignModal] = useState(false);

  // Image + campaigns reload only on salonId change
  useEffect(() => {
    loadSalonHomeImage();
    loadCampaignMetrics();
  }, [salonId]);

  // Revenue, operational metrics + targets reload on salonId OR selectedLocationId change
  useEffect(() => {
    loadTargets(selectedLocationId);
    loadRevenueMetrics(selectedLocationId);
    loadOperationalMetrics(selectedLocationId);
  }, [salonId, selectedLocationId]);

  const loadSalonHomeImage = async () => {
    try {
      setLoading(true);

      // Primary: read hero + logo URLs from Firestore (same source as Customer Portal)
      const salonDoc = await getDoc(doc(db, 'salons', salonId));
      if (salonDoc.exists()) {
        const data = salonDoc.data();
        const heroUrl = data?.branding?.images?.hero;
        const logo   = data?.branding?.logo?.url;
        if (logo) setLogoUrl(logo);
        if (heroUrl) {
          setImageUrl(heroUrl);
          return;
        }
      }

      // Fallback 1: list Storage files and pick the most recently uploaded
      try {
        const heroFolderRef = ref(storage, `salons/${salonId}/hero`);
        const listResult = await listAll(heroFolderRef);
        if (listResult.items.length > 0) {
          // Sort by name descending (filenames are timestamp-prefixed)
          const sorted = [...listResult.items].sort((a, b) =>
            b.name.localeCompare(a.name)
          );
          const url = await getDownloadURL(sorted[0]);
          setImageUrl(url);
          return;
        }
      } catch {
        // Storage folder may not exist yet — continue to next fallback
      }

      // Fallback 2: legacy salonHomeImages collection
      const homeImageDoc = await getDoc(doc(db, 'salonHomeImages', salonId));
      if (homeImageDoc.exists()) {
        setImageUrl(homeImageDoc.data().imageUrl);
      } else {
        setImageUrl('');
      }
    } catch (err) {
      console.error('Error loading salon home image:', err);
      setImageUrl('');
    } finally {
      setLoading(false);
    }
  };

  const loadTargets = async (locationId = null) => {
    try {
      const salonDoc = await getDoc(doc(db, 'salons', salonId));
      if (salonDoc.exists()) {
        const data = salonDoc.data();
        const t = locationId
          ? (data.locationTargets?.[locationId] || data.targets || {})
          : (data.targets || {});
        setTargets(t);
      }
    } catch (err) {
      console.error('Error loading targets:', err);
    }
  };

  const loadRevenueMetrics = async (locationId = null) => {
    try {
      setMetricsLoading(true);
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      monthStart.setHours(0, 0, 0, 0);
      const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      monthEnd.setHours(23, 59, 59, 999);

      const weekStart = getWeekStart();
      const weekEnd = getWeekEnd();

      // Fetch all transactions for this salon — paymentStatus is filtered
      // client-side to accept both 'Completed' and 'completed' (case-insensitive)
      const q = query(
        collection(db, 'transactions'),
        where('salonId', '==', salonId)
      );

      const snapshot = await getDocs(q);
      let monthRev = 0;
      let weekRev = 0;

      snapshot.forEach((docSnap) => {
        const tx = docSnap.data();

        // Skip non-completed transactions (case-insensitive)
        const status = (tx.paymentStatus || '').toLowerCase();
        if (status !== 'completed') return;

        // Location filter (client-side)
        if (locationId && tx.locationId !== locationId) return;

        const amount = parseFloat(tx.amount || tx.totalAmount || tx.price || 0);

        let txDate = null;
        const raw = tx.paidAt || tx.createdAt;
        if (raw) {
          if (raw.toDate) txDate = raw.toDate();
          else if (typeof raw === 'string') txDate = new Date(raw);
          else if (raw.seconds) txDate = new Date(raw.seconds * 1000);
        }

        if (txDate) {
          if (txDate >= monthStart && txDate <= monthEnd) monthRev += amount;
          if (txDate >= weekStart && txDate <= weekEnd) weekRev += amount;
        }
      });

      setCurrentMonthRevenue(monthRev);
      setCurrentWeekRevenue(weekRev);
    } catch (err) {
      console.error('Error loading revenue metrics:', err);
    } finally {
      setMetricsLoading(false);
    }
  };

  const loadOperationalMetrics = async (locationId = null) => {
    try {
      setOperationalLoading(true);
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
      const todayEnd   = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

      const snapshot = await getDocs(
        query(collection(db, 'appointments'), where('salonId', '==', salonId))
      );

      let todayCount  = 0;
      let pendingCount = 0;
      let nextAppt    = null;
      const todayAppts = [];

      snapshot.forEach((docSnap) => {
        const data = docSnap.data();

        // Location filter (client-side)
        if (locationId && data.locationId !== locationId) return;

        // Parse appointment date
        let apptDate = null;
        const raw = data.dateTimeRequested || data.startDateTime;
        if (raw) {
          if (raw.toDate)              apptDate = raw.toDate();
          else if (typeof raw === 'string') apptDate = new Date(raw);
          else if (raw.seconds)        apptDate = new Date(raw.seconds * 1000);
        }

        // Pending requests (all dates)
        if (data.status === 'pending') pendingCount++;

        // Today's non-cancelled/rejected appointments
        if (apptDate && apptDate >= todayStart && apptDate <= todayEnd &&
            data.status !== 'cancelled' && data.status !== 'rejected') {
          todayCount++;
          todayAppts.push({
            id: docSnap.id,
            time: apptDate,
            customerName:  data.customerName || data.customerEmail?.split('@')[0] || 'Client',
            serviceName:   data.serviceRequested?.name || data.serviceName || 'Service',
            staffName:     data.staffName || '',
            status:        data.status || 'pending',
            checkInStatus: data.checkInStatus || null,
            duration:      data.serviceRequested?.duration || null,
          });
        }

        // Next upcoming approved appointment after now
        if (data.status === 'approved' && apptDate && apptDate > now) {
          if (!nextAppt || apptDate < nextAppt.time) {
            nextAppt = {
              time:         apptDate,
              customerName: data.customerName || data.customerEmail?.split('@')[0] || 'Client',
              serviceName:  data.serviceRequested?.name || data.serviceName || 'Service',
              staffName:    data.staffName || '',
            };
          }
        }
      });

      todayAppts.sort((a, b) => a.time - b.time);
      setTodayAppointments(todayCount);
      setPendingRequests(pendingCount);
      setNextAppointment(nextAppt);
      setTodaySchedule(todayAppts);
    } catch (err) {
      console.error('Error loading operational metrics:', err);
      setTodayAppointments(0);
      setPendingRequests(0);
      setNextAppointment(null);
      setTodaySchedule([]);
    } finally {
      setOperationalLoading(false);
    }
  };

  const loadCampaignMetrics = async () => {
    try {
      const colTypeMap = {
        mail: 'Email Campaign',
        pushNotifications: 'Push Notification',
        whatsappMessages: 'WhatsApp Campaign',
      };
      let allSent = [];

      for (const [col, displayType] of Object.entries(colTypeMap)) {
        try {
          const snap = await getDocs(
            query(
              collection(db, col),
              where('salonId', '==', salonId),
              where('metadata.campaignType', '==', 'actual_blast')
            )
          );
          snap.forEach((docSnap) => {
            const data = docSnap.data();
            if (data.status === 'sent' && data.metadata?.timestamp) {
              const name = data.metadata?.originalSubject || data.subject || data.title || 'Campaign';
              allSent.push({
                name,
                sentAt: new Date(data.metadata.timestamp),
                displayType,
                body: data.body || data.text || '',
                targetingType: data.metadata?.targetingType || 'all_customers',
                targetingCriteria: data.metadata?.targetingCriteria || {},
                schedulingType: data.metadata?.schedulingType || 'one-time',
                scheduledDate: data.metadata?.scheduledDate || '',
                scheduledTime: data.metadata?.scheduledTime || '',
                createdAt: data.metadata?.timestamp ? new Date(data.metadata.timestamp) : null,
              });
            }
          });
        } catch (e) {
          // Collection may not exist for this salon — safe to continue
        }
      }

      if (allSent.length > 0) {
        allSent.sort((a, b) => b.sentAt - a.sentAt);
        setLastCampaign(allSent[0]);
      }
    } catch (err) {
      console.error('Error loading campaign metrics:', err);
      setLastCampaign(null);
    }
  };

  const handleScheduleCheckIn = async (appointmentId, status) => {
    try {
      await updateDoc(doc(db, 'appointments', appointmentId), {
        checkInStatus: status,
        checkInAt: new Date(),
        updatedAt: new Date(),
      });
      setTodaySchedule(prev =>
        prev.map(a => a.id === appointmentId ? { ...a, checkInStatus: status } : a)
      );
      setScheduleCheckInOpen(null);
    } catch (error) {
      console.error('Error updating check-in status:', error);
      alert('Failed to update check-in status. Please try again.');
    }
  };

  const formatApptTime = (date) => {
    if (!date) return '—';
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const formatNextApptDateTime = (date) => {
    if (!date) return '—';
    const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    const day  = date.toLocaleDateString('en-US', { weekday: 'short' });
    const d    = date.getDate();
    const mon  = date.toLocaleDateString('en-US', { month: 'short' });
    return `${time}, ${day}, ${d} ${mon}`;
  };

  const getRelativeTime = (date) => {
    if (!date) return '';
    const days = Math.floor((Date.now() - date.getTime()) / 86400000);
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7)  return `${days} days ago`;
    const weeks = Math.floor(days / 7);
    return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
  };

  const getStaffColour = (name) => {
    const palette = ['#E64C31','#22C55E','#8B5CF6','#3B82F6','#F59E0B','#EC4899','#14B8A6','#F97316'];
    if (!name) return palette[0];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return palette[Math.abs(hash) % palette.length];
  };

  const truncate = (str, n) => (!str || str.length <= n) ? str : str.substring(0, n) + '…';

  const formatTargetingCriteria = (targetingType, criteria) => {
    if (!targetingType || targetingType === 'all_customers' || targetingType === 'not_selected') return 'All Customers';
    if (targetingType === 'specific_customer') {
      const count = criteria?.specificCustomers?.length || 0;
      return `Specific Customers (${count} selected)`;
    }
    if (targetingType === 'customer_profile') {
      const parts = [];
      if (criteria?.birthMonths?.length) parts.push(`Birth Month(s): ${criteria.birthMonths.join(', ')}`);
      if (criteria?.postalCodes?.length) parts.push(`Postal Code(s): ${criteria.postalCodes.join(', ')}`);
      return parts.length ? parts.join(' | ') : 'Customer Profile';
    }
    if (targetingType === 'customer_transaction') {
      const parts = [];
      if (criteria?.transactionCount != null) parts.push(`Transactions: ${criteria.transactionType === 'greater_than' ? 'more than' : 'less than'} ${criteria.transactionCount}`);
      if (criteria?.lastTransactionMonths) parts.push(`In last ${criteria.lastTransactionMonths} month(s)`);
      return parts.length ? parts.join(' | ') : 'Transaction Based';
    }
    if (targetingType === 'customer_points') {
      const parts = [];
      if (criteria?.memberTiers?.length) parts.push(`Tier(s): ${criteria.memberTiers.join(', ')}`);
      if (criteria?.pointsAmount != null) parts.push(`Points: ${criteria.pointsType === 'greater_than' ? 'more than' : 'less than'} ${criteria.pointsAmount}`);
      return parts.length ? parts.join(' | ') : 'Points/Loyalty Based';
    }
    return targetingType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const handleImageUpload = async (event) => {
    const { uploadBytes } = await import('firebase/storage');
    const { setDoc } = await import('firebase/firestore');
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size should be less than 5MB');
      return;
    }

    try {
      setUploading(true);
      setError('');
      const imageRef = ref(storage, `salon-home-images/${salonId}.jpg`);
      await uploadBytes(imageRef, file);
      const url = await getDownloadURL(imageRef);
      await setDoc(doc(db, 'salonHomeImages', salonId), {
        imageUrl: url,
        updatedAt: new Date()
      });
      setImageUrl(url);
    } catch (err) {
      console.error('Error uploading image:', err);
      setError('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const getMonthlyPercent = () => {
    const monthKey = getCurrentMonthKey();
    const periodTargets = targets.monthlyPeriodTargets || [];
    const periodMatch = periodTargets.find((p) => p.period === monthKey);
    const effectiveTarget = periodMatch ? periodMatch.target : targets.monthlyTarget;
    if (!effectiveTarget || effectiveTarget === 0) return null;
    return ((currentMonthRevenue / effectiveTarget) * 100).toFixed(1);
  };

  const getWeeklyPercent = () => {
    const weekKey = getCurrentISOWeek();
    const periodTargets = targets.weeklyPeriodTargets || [];
    const periodMatch = periodTargets.find((p) => p.period === weekKey);
    const effectiveTarget = periodMatch
      ? periodMatch.target
      : (targets.weeklyTarget || 0);
    if (!effectiveTarget || effectiveTarget === 0) return null;
    return ((currentWeekRevenue / effectiveTarget) * 100).toFixed(1);
  };

  if (loading) {
    return (
      <div className="salon-home-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  const monthlyPercent = getMonthlyPercent();
  const weeklyPercent = getWeeklyPercent();

  return (
    <div className="salon-home-page">

      {imageUrl ? (
        <div className="salon-fullscreen-container">
          <img
            src={imageUrl}
            alt="Salon"
            className="salon-fullscreen-image"
          />

          {!salonId.startsWith('demo-') && (
            <div className="image-actions">
              <label className="change-image-button">
                Change Image
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                />
              </label>
            </div>
          )}

          {/* Revenue Overlay */}
          <div className="revenue-overlay">
            {isMultiLocation && !locLoading && (
              <div className="location-filter-bar">
                <select
                  className="location-filter-select"
                  value={selectedLocationId || ''}
                  onChange={e => setSelectedLocationId(e.target.value || null)}
                >
                  <option value="">All Locations</option>
                  {locations.map(loc => (
                    <option key={loc.locationId} value={loc.locationId}>
                      {loc.locationName}
                    </option>
                  ))}
                </select>
              </div>
            )}

          {metricsLoading ? (
              <div className="overlay-loading">
                <div className="small-spinner"></div>
              </div>
            ) : (
              <div className="overlay-metrics-grid">
                {/* Row 1 — Financial metrics */}
                <div className="overlay-metrics-row overlay-metrics-row--financial">
                  <div className="overlay-metric-card">
                    <span className="overlay-metric-label">Current Month Revenue</span>
                    <span className="overlay-metric-value">
                      ${currentMonthRevenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </span>
                  </div>

                  <div className="overlay-metric-card">
                    <span className="overlay-metric-label">% of Monthly Target</span>
                    <span className="overlay-metric-value">
                      {monthlyPercent !== null ? `${monthlyPercent}%` : 'No target set'}
                    </span>
                  </div>

                  <div className="overlay-metric-card">
                    <span className="overlay-metric-label">Current Week Revenue</span>
                    <span className="overlay-metric-value">
                      ${currentWeekRevenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </span>
                  </div>

                  <div className="overlay-metric-card">
                    <span className="overlay-metric-label">% of Weekly Target</span>
                    <span className="overlay-metric-value">
                      {weeklyPercent !== null ? `${weeklyPercent}%` : 'No target set'}
                    </span>
                  </div>
                </div>

                {/* Row 2 — Operational metrics */}
                <div className="overlay-metrics-row overlay-metrics-row--operational">
                  <div className="overlay-metric-card overlay-metric-card--operational">
                    <span className="overlay-metric-label">Today's Appointments</span>
                    {operationalLoading
                      ? <span className="overlay-metric-skeleton" />
                      : <>
                          <span className="overlay-metric-value">{todayAppointments}</span>
                          <span className="overlay-metric-sub">
                            {todayAppointments === 0
                              ? 'None today'
                              : `${todaySchedule.filter(a => a.status === 'approved').length} confirmed`}
                          </span>
                        </>
                    }
                  </div>

                  <div className="overlay-metric-card overlay-metric-card--operational">
                    <span className="overlay-metric-label">Next Appointment</span>
                    {operationalLoading
                      ? <span className="overlay-metric-skeleton" />
                      : nextAppointment
                        ? <>
                            <span className="overlay-metric-value overlay-metric-value--sm">
                              {formatNextApptDateTime(nextAppointment.time)}
                            </span>
                            <span className="overlay-metric-sub">
                              {truncate(nextAppointment.customerName, 16)}
                            </span>
                          </>
                        : <span className="overlay-metric-value">—</span>
                    }
                  </div>

                  <div className="overlay-metric-card overlay-metric-card--operational">
                    <span className="overlay-metric-label">Pending Requests</span>
                    {operationalLoading
                      ? <span className="overlay-metric-skeleton" />
                      : <>
                          <span className="overlay-metric-value">
                            {pendingRequests}
                          </span>
                          <span className="overlay-metric-sub">
                            {pendingRequests === 0 ? 'All clear' : 'Needs approval'}
                          </span>
                        </>
                    }
                  </div>

                  <div
                    className={`overlay-metric-card overlay-metric-card--operational${lastCampaign ? ' overlay-metric-card--clickable' : ''}`}
                    onClick={() => lastCampaign && setShowCampaignModal(true)}
                    title={lastCampaign ? 'Click to view campaign details' : undefined}
                  >
                    <span className="overlay-metric-label">Last Campaign</span>
                    {operationalLoading
                      ? <span className="overlay-metric-skeleton" />
                      : lastCampaign
                        ? <>
                            <span className="overlay-metric-value overlay-metric-value--sm" title={lastCampaign.name}>
                              {truncate(lastCampaign.name, 14)}
                            </span>
                            <span className="overlay-metric-sub">{getRelativeTime(lastCampaign.sentAt)}</span>
                          </>
                        : <span className="overlay-metric-value overlay-metric-value--sm">None sent yet</span>
                    }
                  </div>
                </div>
              </div>
            )}

            {!operationalLoading && (
              <div className="salon-home-schedule">
                <div
                  className="salon-home-schedule-label salon-home-schedule-label--clickable"
                  onClick={() => setShowScheduleModal(true)}
                  title="Click to view full schedule and check in appointments"
                >
                  {selectedLocationId
                    ? `Today's Schedule — ${locations.find(l => l.locationId === selectedLocationId)?.locationName || ''}`
                    : "Today's Schedule"}
                  <span className="salon-home-schedule-label-icon">↗</span>
                </div>
                <div className="salon-home-schedule-strip">
                  {todaySchedule.length === 0 ? (
                    <div className="salon-home-schedule-empty">No appointments scheduled today</div>
                  ) : (
                    todaySchedule.map((appt, idx) => (
                      <div
                        key={appt.id || idx}
                        className="salon-home-appt-card salon-home-appt-card--clickable"
                        onClick={() => setSelectedAppt(appt)}
                        title="Click to view appointment details"
                      >
                        <div
                          className="salon-home-appt-avatar"
                          style={{ background: getStaffColour(appt.staffName) }}
                        >
                          {appt.staffName ? appt.staffName.charAt(0).toUpperCase() : 'S'}
                        </div>
                        <div className="salon-home-appt-info">
                          <div className="salon-home-appt-time">{formatApptTime(appt.time)}</div>
                          <div className="salon-home-appt-name">{truncate(appt.customerName, 14)}</div>
                          <div className="salon-home-appt-service">{truncate(appt.serviceName, 16)}</div>
                        </div>
                        <div className={`salon-home-appt-status salon-home-appt-status--${appt.status}`}>
                          {appt.status === 'approved' ? 'Confirmed'
                            : appt.status === 'pending' ? 'Pending'
                            : appt.status}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="no-image-container">
          <div className="upload-prompt">
            <h3>Welcome to Your Salon Dashboard</h3>
            <p>Upload an image to personalize your home page</p>

            {error && <div className="error-message">{error}</div>}

            <label className="upload-image-button">
              {uploading ? 'Uploading...' : 'Upload Image'}
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                disabled={uploading}
                style={{ display: 'none' }}
              />
            </label>

            <p className="upload-hint">Recommended: 1920x1080px, Max 5MB</p>
          </div>
        </div>
      )}

      {/* BespokeBeauty copyright footer */}
      <div className="salon-home-footer">
        BespokeBeauty™ © 2026. All Rights Reserved
      </div>

      {/* Appointment Detail Modal */}
      {selectedAppt && (
        <div className="dash-modal-overlay" onClick={() => setSelectedAppt(null)}>
          <div className="dash-modal" onClick={e => e.stopPropagation()}>
            <div className="dash-modal-header">
              <div className="dash-modal-title-row">
                <div
                  className="dash-modal-avatar"
                  style={{ background: getStaffColour(selectedAppt.staffName) }}
                >
                  {selectedAppt.staffName ? selectedAppt.staffName.charAt(0).toUpperCase() : 'S'}
                </div>
                <div>
                  <h3 className="dash-modal-title">{selectedAppt.customerName}</h3>
                  <span className={`dash-modal-status-badge dash-modal-status-badge--${selectedAppt.status}`}>
                    {selectedAppt.status === 'approved' ? 'Confirmed' : selectedAppt.status === 'pending' ? 'Pending' : selectedAppt.status}
                  </span>
                </div>
              </div>
              <button className="dash-modal-close" onClick={() => setSelectedAppt(null)}>×</button>
            </div>
            <div className="dash-modal-body">
              <div className="dash-modal-field">
                <span className="dash-modal-field-label">Customer Name</span>
                <span className="dash-modal-field-value">{selectedAppt.customerName}</span>
              </div>
              <div className="dash-modal-field">
                <span className="dash-modal-field-label">Staff</span>
                <span className="dash-modal-field-value">{selectedAppt.staffName || '—'}</span>
              </div>
              <div className="dash-modal-field">
                <span className="dash-modal-field-label">Date</span>
                <span className="dash-modal-field-value">
                  {selectedAppt.time
                    ? selectedAppt.time.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
                    : '—'}
                </span>
              </div>
              <div className="dash-modal-field">
                <span className="dash-modal-field-label">Time</span>
                <span className="dash-modal-field-value">{formatApptTime(selectedAppt.time)}</span>
              </div>
              <div className="dash-modal-field">
                <span className="dash-modal-field-label">Service</span>
                <span className="dash-modal-field-value">{selectedAppt.serviceName}</span>
              </div>
              {selectedAppt.duration && (
                <div className="dash-modal-field">
                  <span className="dash-modal-field-label">Duration</span>
                  <span className="dash-modal-field-value">{selectedAppt.duration} min</span>
                </div>
              )}
              {selectedAppt.checkInStatus && (
                <div className="dash-modal-field">
                  <span className="dash-modal-field-label">Check-in</span>
                  <span className={`dash-modal-checkin-badge dash-modal-checkin-badge--${selectedAppt.checkInStatus === 'Showed Up' ? 'showed' : 'noshow'}`}>
                    {selectedAppt.checkInStatus}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Last Campaign Detail Modal */}
      {showCampaignModal && lastCampaign && (
        <div className="dash-modal-overlay" onClick={() => setShowCampaignModal(false)}>
          <div className="dash-modal" onClick={e => e.stopPropagation()}>
            <div className="dash-modal-header">
              <div className="dash-modal-title-row">
                <div className="dash-modal-campaign-icon">
                  {lastCampaign.displayType === 'Push Notification' ? '🔔'
                    : lastCampaign.displayType === 'WhatsApp Campaign' ? '💬'
                    : '✉️'}
                </div>
                <div>
                  <h3 className="dash-modal-title">{lastCampaign.name}</h3>
                  <div className="dash-modal-badges">
                    <span className="dash-modal-status-badge dash-modal-status-badge--sent">SENT</span>
                    <span className="dash-modal-type-badge">{lastCampaign.displayType}</span>
                  </div>
                </div>
              </div>
              <button className="dash-modal-close" onClick={() => setShowCampaignModal(false)}>×</button>
            </div>
            {lastCampaign.body && (
              <div className="dash-modal-campaign-body">
                {lastCampaign.body}
              </div>
            )}
            <div className="dash-modal-body">
              <div className="dash-modal-field">
                <span className="dash-modal-field-label">Created</span>
                <span className="dash-modal-field-value">
                  {lastCampaign.createdAt ? lastCampaign.createdAt.toLocaleString() : '—'}
                </span>
              </div>
              <div className="dash-modal-field">
                <span className="dash-modal-field-label">Targeting Criteria</span>
                <span className="dash-modal-field-value">
                  {formatTargetingCriteria(lastCampaign.targetingType, lastCampaign.targetingCriteria)}
                </span>
              </div>
              <div className="dash-modal-field">
                <span className="dash-modal-field-label">Schedule Type</span>
                <span className="dash-modal-field-value">
                  {lastCampaign.schedulingType === 'recurring' ? 'Recurring' : 'One-time'}
                </span>
              </div>
              <div className="dash-modal-field">
                <span className="dash-modal-field-label">Scheduled</span>
                <span className="dash-modal-field-value">
                  {lastCampaign.scheduledDate && lastCampaign.scheduledTime
                    ? new Date(`${lastCampaign.scheduledDate}T${lastCampaign.scheduledTime}`).toLocaleString()
                    : '—'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Today's Schedule Modal */}
      {showScheduleModal && (
        <div className="schedule-modal-overlay" onClick={() => { setShowScheduleModal(false); setScheduleCheckInOpen(null); }}>
          <div className="schedule-modal" onClick={e => e.stopPropagation()}>
            <div className="schedule-modal-header">
              <h3>
                {selectedLocationId
                  ? `Today's Schedule — ${locations.find(l => l.locationId === selectedLocationId)?.locationName || ''}`
                  : "Today's Schedule"}
              </h3>
              <button className="schedule-modal-close" onClick={() => { setShowScheduleModal(false); setScheduleCheckInOpen(null); }}>×</button>
            </div>

            <div className="schedule-modal-body">
              {todaySchedule.filter(a => a.status === 'approved').length === 0 ? (
                <div className="schedule-modal-empty">No confirmed appointments for today.</div>
              ) : (
                todaySchedule.filter(a => a.status === 'approved').map((appt, idx) => (
                  <div key={appt.id || idx} className="schedule-modal-row">
                    <div
                      className="schedule-modal-avatar"
                      style={{ background: getStaffColour(appt.staffName) }}
                    >
                      {appt.staffName ? appt.staffName.charAt(0).toUpperCase() : 'S'}
                    </div>
                    <div className="schedule-modal-info">
                      <div className="schedule-modal-time">{formatApptTime(appt.time)}</div>
                      <div className="schedule-modal-customer">{appt.customerName}</div>
                      <div className="schedule-modal-service">
                        {appt.serviceName}
                        {appt.duration ? ` · ${appt.duration} min` : ''}
                        {appt.staffName ? ` · ${appt.staffName}` : ''}
                      </div>
                    </div>
                    <div className="schedule-modal-checkin">
                      {scheduleCheckInOpen === appt.id ? (
                        <div className="checkin-dropdown">
                          <select
                            className="checkin-select"
                            value={scheduleCheckInValue}
                            onChange={e => setScheduleCheckInValue(e.target.value)}
                          >
                            <option value="Showed Up">Showed Up</option>
                            <option value="No-Show">No-Show</option>
                          </select>
                          <button
                            className="checkin-confirm-btn"
                            onClick={() => handleScheduleCheckIn(appt.id, scheduleCheckInValue)}
                          >
                            Confirm
                          </button>
                          <button
                            className="checkin-cancel-inline-btn"
                            onClick={() => setScheduleCheckInOpen(null)}
                          >
                            ×
                          </button>
                        </div>
                      ) : appt.checkInStatus ? (
                        <div className="schedule-modal-checkin-done">
                          <span className={`checkin-status-badge checkin-status-badge--${appt.checkInStatus === 'Showed Up' ? 'showed' : 'noshow'}`}>
                            {appt.checkInStatus}
                          </span>
                          <button
                            className="checkin-btn checkin-btn--sm"
                            onClick={() => { setScheduleCheckInValue(appt.checkInStatus); setScheduleCheckInOpen(appt.id); }}
                          >
                            Change
                          </button>
                        </div>
                      ) : (
                        <button
                          className="checkin-btn"
                          onClick={() => { setScheduleCheckInValue('Showed Up'); setScheduleCheckInOpen(appt.id); }}
                        >
                          Check In
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SalonHomePage;
